import { useState, useCallback } from 'react';
import {
  detectFacingDirection as apiFetchFacingDirection,
  type FacingDetectionResponse,
} from '@/services/facingApi';
import { CONFIDENCE } from '@/utils/constants';

export interface UseFacingDetection {
  direction: 'left' | 'right' | null;
  confidence: number;
  isLoading: boolean;
  error: string | null;

  // State flags based on confidence
  isAutoApplied: boolean;      // confidence > 0.85
  needsConfirmation: boolean;  // confidence 0.50-0.85
  needsManualSelection: boolean; // confidence < 0.50

  // Actions
  detectFacing: (videoId: string) => Promise<void>;
  confirmDirection: (dir: 'left' | 'right') => void;
  reset: () => void;
}

/**
 * Hook for detecting and managing facing direction with smart fallback logic
 *
 * Implements Option A (auto with smart fallback):
 * - confidence > 85%: auto-apply direction
 * - confidence 50-85%: show confirmation dialog
 * - confidence < 50%: require manual selection
 *
 * @example
 * ```tsx
 * const {
 *   direction,
 *   confidence,
 *   isAutoApplied,
 *   needsConfirmation,
 *   needsManualSelection,
 *   detectFacing,
 *   confirmDirection,
 * } = useFacingDetection();
 *
 * // Detect facing when video is uploaded
 * await detectFacing(videoId);
 *
 * // Handle confirmation if needed
 * if (needsConfirmation) {
 *   // Show confirmation dialog
 *   confirmDirection('left'); // or 'right'
 * }
 * ```
 */
export function useFacingDetection(): UseFacingDetection {
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const [confidence, setConfidence] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAutoApplied, setIsAutoApplied] = useState<boolean>(false);

  // Derived state flags
  const needsConfirmation =
    confidence >= CONFIDENCE.NEEDS_REVIEW &&
    confidence < CONFIDENCE.AUTO_APPLY &&
    direction !== null &&
    !isAutoApplied;

  const needsManualSelection =
    confidence < CONFIDENCE.NEEDS_REVIEW &&
    direction !== null &&
    !isAutoApplied;

  /**
   * Detect facing direction from video
   */
  const detectFacing = useCallback(async (videoId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setIsAutoApplied(false);

    try {
      const result: FacingDetectionResponse = await apiFetchFacingDirection(videoId);

      setDirection(result.direction);
      setConfidence(result.confidence);

      // Auto-apply if confidence is high enough
      if (result.confidence >= CONFIDENCE.AUTO_APPLY) {
        setIsAutoApplied(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to detect facing direction';
      setError(errorMessage);
      setDirection(null);
      setConfidence(0);
      setIsAutoApplied(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Confirm or override the detected direction
   * Used when user confirms medium-confidence detection or manually selects
   */
  const confirmDirection = useCallback((dir: 'left' | 'right'): void => {
    setDirection(dir);
    setConfidence(1.0); // Manual confirmation = 100% confidence
    setIsAutoApplied(true); // Mark as applied
    setError(null);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback((): void => {
    setDirection(null);
    setConfidence(0);
    setIsLoading(false);
    setError(null);
    setIsAutoApplied(false);
  }, []);

  return {
    direction,
    confidence,
    isLoading,
    error,
    isAutoApplied,
    needsConfirmation,
    needsManualSelection,
    detectFacing,
    confirmDirection,
    reset,
  };
}
